/**
 * Applications Copilot — text-to-speech layer.
 *
 * Generalises src/LiveInterview/utils/liveInterviewFeedbackTts.js with two
 * differences required by the design:
 *   1. Credentials come from GET /api/speech/token and are applied with
 *      SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region). The
 *      Azure subscription key never reaches the browser. The token is
 *      refreshed 60 s before `expires_in`.
 *   2. The SDK is imported lazily (dynamic import) so the Applications page
 *      bundle does not grow for users who never turn voice on.
 *
 * Falls back to window.speechSynthesis when the token endpoint returns 503
 * (Azure not configured) or when the SDK fails.
 *
 * API: speak(text), enqueue(sentence), flush(), stop(), setEnabled(bool),
 * isEnabled(), subscribe(listener), splitSentences(text).
 */
import { fetchSpeechToken } from './copilotApi';

const STORAGE_KEY = 'copilot.tts';
const TOKEN_SKEW_MS = 60 * 1000;
const LANGUAGE = 'en-IN';

let playToken = 0;
let enabled = readEnabled();
let speakingState = false;
const listeners = new Set();

let sdkPromise = null;
let tokenCache = null; // { token, region, expiresAt }
let tokenPromise = null;
let azureUnavailableUntil = 0;

let queue = [];
let partial = '';
let draining = false;

let activeSynthesizer = null;
let activeAudio = null;
let activeObjectUrl = '';
let activeUtterance = null;

// ---------------------------------------------------------------------------
// enabled flag (persisted)
// ---------------------------------------------------------------------------

function readEnabled() {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    return raw === '1' || raw === 'true';
  } catch {
    return false;
  }
}

export function isEnabled() {
  return enabled;
}

export function setEnabled(value) {
  enabled = Boolean(value);
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // ignore storage failures (private mode)
  }
  if (!enabled) stop();
  emit();
}

export function isSpeaking() {
  return speakingState;
}

/** Subscribe to { speaking, enabled } changes. Returns unsubscribe. */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit() {
  const snapshot = { speaking: speakingState, enabled };
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      // listener errors must not break playback
    }
  });
}

function setSpeaking(value) {
  if (speakingState === value) return;
  speakingState = value;
  emit();
}

// ---------------------------------------------------------------------------
// Azure credentials
// ---------------------------------------------------------------------------

async function loadSdk() {
  if (!sdkPromise) {
    sdkPromise = import('microsoft-cognitiveservices-speech-sdk').catch((err) => {
      sdkPromise = null;
      throw err;
    });
  }
  return sdkPromise;
}

/**
 * Get a valid { token, region } pair, refreshing 60 s before expiry.
 * Returns null when Azure speech is not configured (503) or unreachable.
 */
export async function getSpeechCredentials() {
  const now = Date.now();
  if (tokenCache && tokenCache.expiresAt - TOKEN_SKEW_MS > now) return tokenCache;
  if (azureUnavailableUntil > now) return null;
  if (!tokenPromise) {
    tokenPromise = (async () => {
      try {
        const data = await fetchSpeechToken();
        if (!data?.token || !data?.region) throw new Error('Incomplete speech token');
        const ttl = Math.max(90, Number(data.expires_in) || 540) * 1000;
        tokenCache = { token: data.token, region: data.region, expiresAt: Date.now() + ttl };
        return tokenCache;
      } catch (err) {
        const status = err?.response?.status;
        // 503 = not configured; back off for 5 min. Other failures: 30 s.
        azureUnavailableUntil = Date.now() + (status === 503 ? 5 * 60 * 1000 : 30 * 1000);
        return null;
      } finally {
        tokenPromise = null;
      }
    })();
  }
  return tokenPromise;
}

/** Build a SpeechConfig from a short-lived authorization token (never a key). */
export async function createSpeechConfig() {
  const creds = await getSpeechCredentials();
  if (!creds) return null;
  const SpeechSDK = await loadSdk();
  if (!SpeechSDK?.SpeechConfig?.fromAuthorizationToken) return null;
  const config = SpeechSDK.SpeechConfig.fromAuthorizationToken(creds.token, creds.region);
  config.speechRecognitionLanguage = LANGUAGE;
  config.speechSynthesisLanguage = LANGUAGE;
  config.speechSynthesisVoiceName = 'en-IN-NeerjaNeural';
  return { SpeechSDK, config, creds };
}

// ---------------------------------------------------------------------------
// playback primitives
// ---------------------------------------------------------------------------

function isCurrent(token) {
  return token === playToken;
}

function closeSynthesizer(synth) {
  if (!synth) return;
  try {
    synth.close();
  } catch {
    // best effort
  }
}

export function stop() {
  playToken += 1;
  queue = [];
  partial = '';
  draining = false;

  const synth = activeSynthesizer;
  activeSynthesizer = null;
  closeSynthesizer(synth);

  if (activeAudio) {
    try {
      activeAudio.pause();
      activeAudio.removeAttribute('src');
      activeAudio.load?.();
    } catch {
      // best effort
    }
    activeAudio = null;
  }
  if (activeObjectUrl) {
    try {
      URL.revokeObjectURL(activeObjectUrl);
    } catch {
      // best effort
    }
    activeObjectUrl = '';
  }
  activeUtterance = null;
  try {
    window.speechSynthesis?.cancel?.();
  } catch {
    // best effort
  }
  setSpeaking(false);
}

function playAudioBuffer(audioData, token) {
  return new Promise((resolve) => {
    if (!audioData || audioData.byteLength < 44 || typeof Audio === 'undefined') {
      resolve(false);
      return;
    }
    const blob = new Blob([audioData], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    activeObjectUrl = url;
    const audio = new Audio(url);
    activeAudio = audio;
    const finish = (ok) => {
      audio.onended = null;
      audio.onerror = null;
      if (activeObjectUrl === url) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
        activeObjectUrl = '';
      }
      resolve(ok && isCurrent(token));
    };
    audio.onended = () => finish(true);
    audio.onerror = () => finish(false);
    audio.play().catch(() => finish(false));
  });
}

async function speakWithAzure(text, token) {
  let ctx;
  try {
    ctx = await createSpeechConfig();
  } catch {
    return false;
  }
  if (!ctx || !isCurrent(token)) return false;
  const { SpeechSDK, config } = ctx;
  return new Promise((resolve) => {
    try {
      const synthesizer = new SpeechSDK.SpeechSynthesizer(config, null);
      activeSynthesizer = synthesizer;
      synthesizer.speakTextAsync(
        text,
        (result) => {
          closeSynthesizer(synthesizer);
          if (activeSynthesizer === synthesizer) activeSynthesizer = null;
          if (!isCurrent(token)) {
            resolve(false);
            return;
          }
          const failed =
            SpeechSDK.ResultReason && result?.reason === SpeechSDK.ResultReason.Canceled;
          if (failed || !result?.audioData) {
            resolve(false);
            return;
          }
          playAudioBuffer(result.audioData, token).then(resolve);
        },
        () => {
          closeSynthesizer(synthesizer);
          if (activeSynthesizer === synthesizer) activeSynthesizer = null;
          resolve(false);
        }
      );
    } catch {
      resolve(false);
    }
  });
}

function speakWithBrowser(text, token) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
      resolve(false);
      return;
    }
    let voices = [];
    try {
      voices = window.speechSynthesis.getVoices?.() || [];
    } catch {
      voices = [];
    }
    const preferred =
      voices.find((v) => /^en[-_]IN$/i.test(v.lang || '')) ||
      voices.find((v) => /^en[-_]US$/i.test(v.lang || '')) ||
      voices.find((v) => /^en/i.test(v.lang || '')) ||
      null;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.lang = preferred?.lang || LANGUAGE;
    if (preferred) utterance.voice = preferred;
    activeUtterance = utterance;
    let started = false;
    const finish = (ok) => {
      if (activeUtterance === utterance) activeUtterance = null;
      resolve(Boolean(ok && started && isCurrent(token)));
    };
    utterance.onstart = () => {
      started = true;
    };
    utterance.onend = () => finish(true);
    utterance.onerror = () => finish(false);
    try {
      window.speechSynthesis.speak(utterance);
      window.speechSynthesis.resume?.();
    } catch {
      finish(false);
    }
  });
}

async function speakOne(text, token) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean || !isCurrent(token)) return false;
  const viaAzure = await speakWithAzure(clean, token);
  if (viaAzure || !isCurrent(token)) return viaAzure;
  return speakWithBrowser(clean, token);
}

// ---------------------------------------------------------------------------
// sentence queue
// ---------------------------------------------------------------------------

/**
 * Split text into complete sentences + unterminated remainder.
 * Also strips light markdown so it is not read aloud.
 */
export function splitSentences(text) {
  const clean = String(text || '')
    .replace(/\*\*|__|`/g, '')
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    .replace(/\[([^\]]+)\]/g, '$1');
  const sentences = [];
  const re = /[^.!?\n]+[.!?]+["')\]]*\s*|[^\n]+\n+/g;
  let last = 0;
  let m = re.exec(clean);
  while (m) {
    const s = m[0].trim();
    if (s) sentences.push(s);
    last = m.index + m[0].length;
    m = re.exec(clean);
  }
  return { sentences, rest: clean.slice(last) };
}

async function drain() {
  if (draining) return;
  draining = true;
  const token = playToken;
  setSpeaking(true);
  try {
    while (queue.length && isCurrent(token) && enabled) {
      const next = queue.shift();
      // eslint-disable-next-line no-await-in-loop
      await speakOne(next, token);
    }
  } finally {
    if (isCurrent(token)) {
      draining = false;
      if (!queue.length) setSpeaking(false);
      else void drain();
    }
  }
}

/** Queue a sentence (or delta text); playback starts after the first full sentence. */
export function enqueue(textChunk) {
  if (!enabled || !textChunk) return;
  partial += textChunk;
  const { sentences, rest } = splitSentences(partial);
  partial = rest;
  if (!sentences.length) return;
  queue.push(...sentences);
  void drain();
}

/** Speak any buffered partial sentence (call when the stream ends). */
export function flush() {
  if (!enabled) return;
  const rest = partial.trim();
  partial = '';
  if (rest) {
    queue.push(rest);
    void drain();
  }
}

/** Speak a complete text now (interrupts anything playing). */
export async function speak(text, { force = false } = {}) {
  if (!force && !enabled) return false;
  stop();
  const { sentences, rest } = splitSentences(text);
  const all = [...sentences, ...(rest.trim() ? [rest.trim()] : [])];
  if (!all.length) return false;
  queue = all;
  // speak() must work even when the persisted flag is off (explicit replay)
  const wasEnabled = enabled;
  if (force) enabled = true;
  try {
    await drain();
  } finally {
    if (force) enabled = wasEnabled;
  }
  return true;
}

/** Barge-in: stop playback when the user starts typing / talking. */
export function bargeIn() {
  if (speakingState || queue.length || partial) stop();
}

const copilotTts = {
  speak,
  enqueue,
  flush,
  stop,
  bargeIn,
  setEnabled,
  isEnabled,
  isSpeaking,
  subscribe,
  splitSentences,
  getSpeechCredentials,
  createSpeechConfig,
};

export default copilotTts;
